import { useState } from "react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faSave,
  faEye,
  faEyeSlash,
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";

const ChangePasswordTab = ({ user, onSave }) => {
  const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "",
    color: "",
    requirements: {
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false,
    },
  });

  const validatePasswordStrength = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const metCount = Object.values(requirements).filter(Boolean).length;
    let score = 0;
    let label = "";
    let color = "";

    if (password.length === 0) {
      score = 0;
      label = "";
      color = "";
    } else if (metCount <= 2) {
      score = 1;
      label = "Weak";
      color = "text-red-500";
    } else if (metCount === 3) {
      score = 2;
      label = "Medium";
      color = "text-yellow-500";
    } else if (metCount >= 4) {
      score = 3;
      label = "Strong";
      color = "text-green-500";
    }

    setPasswordStrength({
      score,
      label,
      color,
      requirements,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: "" });
    if (name === "newPassword") {
      validatePasswordStrength(value);
    }
  };

  const getStrengthBarColor = () => {
    if (passwordStrength.score === 0) return "bg-gray-200";
    if (passwordStrength.score === 1) return "bg-red-500";
    if (passwordStrength.score === 2) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStrengthBarWidth = () => {
    if (passwordStrength.score === 0) return "0%";
    if (passwordStrength.score === 1) return "33%";
    if (passwordStrength.score === 2) return "66%";
    return "100%";
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (!form.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (form.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    } else if (passwordStrength.score < 3) {
      newErrors.newPassword = "Please use a stronger password";
    }
    if (!form.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (form.newPassword !== form.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({ ...user, password: form.newPassword });
    setForm({ newPassword: "", confirmPassword: "" });
    setPasswordStrength({
      score: 0,
      label: "",
      color: "",
      requirements: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
      },
    });
  };

  return (
    <Card className="bg-white border border-gray-200 shadow-sm">
      <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faLock} className="text-red-800 h-4 w-4" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Change password
            </h3>
            <p className="text-sm text-gray-600">
              Update password for {user.full_name}
            </p>
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-gray-700">
              New Password
            </Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                name="newPassword"
                value={form.newPassword}
                onChange={handleChange}
                placeholder="Enter new password (min 8 characters)"
                className={`pr-10 ${errors.newPassword ? "border-red-500" : "border-gray-300"}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-600"
              >
                <FontAwesomeIcon
                  icon={showPassword ? faEyeSlash : faEye}
                  className="h-4 w-4"
                />
              </button>
            </div>
            {errors.newPassword && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <FontAwesomeIcon
                  icon={faExclamationCircle}
                  className="h-3 w-3"
                />
                {errors.newPassword}
              </p>
            )}

            {form.newPassword.length > 0 && (
              <div className="space-y-3 mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2.5 max-w-xs">
                      <div
                        className={`h-2.5 rounded-full transition-all duration-300 ${getStrengthBarColor()}`}
                        style={{ width: getStrengthBarWidth() }}
                      />
                    </div>
                    <span
                      className={`text-xs font-medium ${passwordStrength.color} min-w-[50px]`}
                    >
                      {passwordStrength.label}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-2 border-t border-gray-200">
                  <div
                    className={`flex items-center gap-1.5 text-xs ${passwordStrength.requirements.length ? "text-green-600" : "text-gray-600"}`}
                  >
                    {passwordStrength.requirements.length ? (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="h-3 w-3 text-green-500"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="h-3 w-3 text-gray-300"
                      />
                    )}
                    <span>At least 8 characters</span>
                  </div>
                  <div
                    className={`flex items-center gap-1.5 text-xs ${passwordStrength.requirements.uppercase ? "text-green-600" : "text-gray-600"}`}
                  >
                    {passwordStrength.requirements.uppercase ? (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="h-3 w-3 text-green-500"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="h-3 w-3 text-gray-300"
                      />
                    )}
                    <span>Uppercase letter (A-Z)</span>
                  </div>
                  <div
                    className={`flex items-center gap-1.5 text-xs ${passwordStrength.requirements.lowercase ? "text-green-600" : "text-gray-600"}`}
                  >
                    {passwordStrength.requirements.lowercase ? (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="h-3 w-3 text-green-500"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="h-3 w-3 text-gray-300"
                      />
                    )}
                    <span>Lowercase letter (a-z)</span>
                  </div>
                  <div
                    className={`flex items-center gap-1.5 text-xs ${passwordStrength.requirements.number ? "text-green-600" : "text-gray-600"}`}
                  >
                    {passwordStrength.requirements.number ? (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="h-3 w-3 text-green-500"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="h-3 w-3 text-gray-300"
                      />
                    )}
                    <span>Number (0-9)</span>
                  </div>
                  <div
                    className={`flex items-center gap-1.5 text-xs col-span-1 sm:col-span-2 ${passwordStrength.requirements.special ? "text-green-600" : "text-gray-600"}`}
                  >
                    {passwordStrength.requirements.special ? (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="h-3 w-3 text-green-500"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="h-3 w-3 text-gray-300"
                      />
                    )}
                    <span>Special character (!@#$%^&*(),.?":{}|&lt;&gt;)</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-gray-700">
              Confirm New Password
            </Label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                className={`pr-10 ${errors.confirmPassword ? "border-red-500" : "border-gray-300"}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-600"
              >
                <FontAwesomeIcon
                  icon={showConfirmPassword ? faEyeSlash : faEye}
                  className="h-4 w-4"
                />
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <FontAwesomeIcon
                  icon={faExclamationCircle}
                  className="h-3 w-3"
                />
                {errors.confirmPassword}
              </p>
            )}
            {form.confirmPassword &&
              form.newPassword === form.confirmPassword &&
              form.newPassword.length > 0 && (
                <p className="text-green-600 text-xs flex items-center gap-1">
                  <FontAwesomeIcon icon={faCheckCircle} className="h-3 w-3" />
                  Passwords match
                </p>
              )}
          </div>

          <div className="flex justify-end pt-2 border-t border-gray-200">
            <Button
              onClick={handleSubmit}
              className="gap-2 bg-blue-800 hover:bg-blue-900 text-white px-8"
            >
              <FontAwesomeIcon icon={faSave} className="h-4 w-4" />
              Update password
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ChangePasswordTab;
